import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../useTranslation';
import AuthService from '../../services/AuthService';
import { PasswordField } from './PasswordField';
import { getAppLanguage } from '../../utils/language';

/** Страница сброса пароля по токену из ссылки в письме (/reset-password?token=...). */
export const ResetPasswordPage: React.FC = () => {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const language = getAppLanguage();
  const { t } = useTranslation(language);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get('token') || '');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t.passwordsDoNotMatch);
      return;
    }
    if (password.length < 6) {
      setError(t.passwordTooShort);
      return;
    }
    if (!token) {
      setError(t.missingResetToken);
      return;
    }
    setLoading(true);
    try {
      await AuthService.resetPassword(token, password, language);
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (err: any) {
      setError(err.message ?? t.resetFailed);
    } finally {
      setLoading(false);
    }
  };

  if (!token && !success) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-4">
        <div className="glass-panel p-6 max-w-md w-full text-center">
          <p className="text-stone-600 dark:text-slate-400">{t.resetPasswordInvalidLink}</p>
          <a href="/" className="mt-4 inline-block text-blue-600 dark:text-blue-400 hover:underline">{t.backToApp}</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-4">
        <div className="glass-panel p-6 max-w-md w-full text-center">
          <p className="text-stone-800 dark:text-white font-medium">{t.resetPasswordSuccess}</p>
          <a href="/" className="mt-4 inline-block text-blue-600 dark:text-blue-400 hover:underline">{t.goToApp}</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center p-4">
      <div className="glass-panel p-6 sm:p-8 max-w-md w-full">
        <h1 className="text-xl font-semibold text-stone-900 dark:text-white mb-2">
          {t.resetPassword}
        </h1>
        <p className="text-sm text-stone-500 dark:text-slate-400 mb-6">{t.resetPasswordSubtitle}</p>
        {error && (
          <div className="bg-red-50/90 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl mb-4 text-sm">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField
            value={password}
            onChange={setPassword}
            label={t.password}
            required
            autoComplete="new-password"
          />
          <PasswordField
            value={confirm}
            onChange={setConfirm}
            label={t.confirmPassword}
            required
            autoComplete="new-password"
            id="reset-confirm-password"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-xl min-h-[48px] disabled:opacity-60"
          >
            {loading ? '...' : t.save}
          </button>
        </form>
        <a href="/" className="mt-4 block text-center text-sm text-stone-500 hover:text-stone-700 dark:hover:text-slate-300">
          {t.backToApp}
        </a>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
